'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { ValidationMessage } from '@/ui/form/validation-message/ValidationMessage.client'
import { Icon } from '@/ui/icons/icon/Icon'
import type { ReactNode } from 'react'
import { useId } from 'react'
import { Checkbox as AriaCheckbox } from 'react-aria-components'

export type CheckboxProps = UiComponentProps<{
  children?: ReactNode
  additionalText?: string
  isSelected?: boolean
  onChange?: (isSelected: boolean) => void
  isDisabled?: boolean
  name?: string
  isInvalid?: boolean
  errorId?: string | false
  shouldDisplayError?: boolean
  errorMessage?: string
  isRequired?: boolean
  slot?: string
}>

const labelClassName =
  'col-start-2 row-start-1 cursor-pointer text-md font-medium leading-light text-brand-2-900 peer-data-[invalid]/checkbox:text-error-fg'

export function Checkbox({
  children,
  additionalText,
  isSelected,
  onChange,
  isDisabled = false,
  name,
  slot,
  isInvalid = false,
  errorId,
  shouldDisplayError = false,
  errorMessage,
  isRequired,
  className,
  id,
  'data-testid': dataTestId,
}: CheckboxProps) {
  const generatedId = useId()
  const checkboxId = id ?? generatedId
  const describedBy = typeof errorId === 'string' ? errorId : undefined

  return (
    <div
      className={twMerge(
        'grid w-full grid-cols-[auto_minmax(0,1fr)] items-start gap-x-1',
        className,
      )}
    >
      <AriaCheckbox
        id={checkboxId}
        name={name}
        isSelected={isSelected}
        onChange={onChange}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        isRequired={isRequired}
        aria-describedby={describedBy}
        data-testid={dataTestId ?? 'checkbox'}
        className="group/checkbox peer/checkbox col-start-1 row-start-1 flex min-h-[1.225rem] cursor-pointer items-center"
        slot={slot}
      >
        <span
          aria-hidden="true"
          className={twMerge(
            'flex size-2 shrink-0 items-center justify-center rounded-xs border-px border-solid border-brand-2-400 bg-white transition-colors duration-100 hd:border-1.5',
            'group-data-[selected]/checkbox:border-brand-2-800 group-data-[selected]/checkbox:bg-brand-2-800',
            'group-data-[disabled]/checkbox:border-brand-2-400 group-data-[disabled]/checkbox:bg-white',
            'group-data-[disabled]/checkbox:group-data-[selected]/checkbox:border-brand-2-400 group-data-[disabled]/checkbox:group-data-[selected]/checkbox:bg-brand-2-400',
            'group-data-[invalid]/checkbox:border-error-fg group-data-[invalid]/checkbox:group-data-[selected]/checkbox:border-error-fg group-data-[invalid]/checkbox:group-data-[selected]/checkbox:bg-error-fg',
            'group-data-[focus-visible]/checkbox:outline group-data-[focus-visible]/checkbox:outline-2 group-data-[focus-visible]/checkbox:outline-offset-2 group-data-[focus-visible]/checkbox:outline-brand-1-700',
          )}
        >
          <Icon.Check
            size="sm"
            className="hidden text-white group-data-[selected]/checkbox:block"
          />
        </span>
      </AriaCheckbox>
      <label
        htmlFor={checkboxId}
        className={twMerge(labelClassName, isDisabled && 'text-brand-2-400')}
      >
        {children}
      </label>
      {additionalText?.trim() ? (
        <span
          className={twMerge(
            'col-start-2 row-start-2 m-0 text-xs font-normal leading-lightest text-brand-2-900',
            isDisabled && 'text-brand-2-400',
          )}
        >
          {additionalText}
        </span>
      ) : null}
      {shouldDisplayError && errorMessage && (
        <ValidationMessage
          className="col-start-2"
          id={typeof errorId === 'string' ? errorId : undefined}
          status="error"
        >
          {errorMessage}
        </ValidationMessage>
      )}
    </div>
  )
}
